'use client';

import { useEffect } from 'react';

import { SPECIAL_OFFER_EVENT_NAME } from '@/app/components/sections/special-offer/tracking';
import { Accordion } from '@/app/components/text-and-data-display/accordion';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { removeEmptyTags } from '@/app/utils/format';
import type { SpecialOffer } from '@/types/special-offer';

interface ConditionsProps extends Pick<SpecialOffer, 'detail' | 'remark'> {
  className?: string;
  isAccordion?: boolean;
}

const CONTENT_CLASS_NAME =
  'where-a:link-light text-html-content-rich where-span:text-inline-wrap';

const Remark = ({ remark = '' }: { remark: string }) => {
  return (
    <div
      className="text-body-2-fixed space-y-2 pt-4 text-gray-700"
      {...createDataAttributes({ test: 'remark' })}
    >
      <h3
        className="font-noto-bold"
        {...createDataAttributes({ test: 'title' })}
      >
        หมายเหตุ
      </h3>
      <div
        className={CONTENT_CLASS_NAME}
        {...createDataAttributes({ test: 'content' })}
        dangerouslySetInnerHTML={{ __html: remark || '' }}
      />
    </div>
  );
};

const AccordionCondition = ({ detail = '', remark = '' }: ConditionsProps) => {
  const isLargeScreen = useMediaQuery('(min-width: 1024px)');
  const [isOpen, { open, close, toggle }] = useDisclosure(false);

  const isBreakpointUnknown = isLargeScreen === undefined;

  useEffect(() => {
    if (isLargeScreen === undefined) return;
    if (isLargeScreen) {
      return open();
    }
    return close();
  }, [isLargeScreen, open, close]);

  return (
    <Accordion
      className={cn('bg-silver-50 border-none text-red-500', {
        'rounded-sm': !isOpen,
        'rounded-t-sm': isOpen,
        'lg:rounded-t-sm lg:rounded-b-none': isBreakpointUnknown,
      })}
      title="เงื่อนไขของรายการ"
      value="condition"
      isOpen={isOpen}
      iconClassName={cn({ 'lg:rotate-180': isBreakpointUnknown })}
      contentClassName={cn('bg-silver-50 rounded-b-sm', {
        'lg:block': isBreakpointUnknown,
      })}
      test="condition"
      onChange={toggle}
      content={
        <>
          <div
            className={cn('text-body-1-fixed', CONTENT_CLASS_NAME)}
            dangerouslySetInnerHTML={{ __html: removeEmptyTags(detail) }}
            {...createDataAttributes({ test: 'detail' })}
          />
          {!!remark && <Remark remark={remark} />}
        </>
      }
      {...createDataGA({
        eventName: SPECIAL_OFFER_EVENT_NAME,
        group: 'condition',
        click: true,
      })}
    />
  );
};

export const Conditions = ({
  className,
  detail = '',
  remark = '',
  isAccordion = false,
}: ConditionsProps) => {
  if (isAccordion) {
    return <AccordionCondition detail={detail} remark={remark} />;
  }

  return (
    <div
      className={cn('mt-2 space-y-6 md:mt-4 lg:mt-0', className)}
      {...createDataAttributes({ test: 'conditions' })}
    >
      <h2
        className="text-sub-h3 font-noto-bold"
        {...createDataAttributes({ test: 'title' })}
      >
        เงื่อนไขของรายการ
      </h2>
      <div
        className={cn('text-body-1-fixed', CONTENT_CLASS_NAME)}
        dangerouslySetInnerHTML={{ __html: removeEmptyTags(detail) }}
        {...createDataAttributes({ test: 'detail' })}
      />
      {!!remark && <Remark remark={remark} />}
    </div>
  );
};
